import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Checkbox,
  CircularProgress,
  Badge,
  Button,
  IconButton,
  Tooltip,
  Box,
  TextField,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  FaCheck,
  FaEdit,
  FaSave,
  FaTimes,
  FaArrowDown,
  FaArrowUp,
} from "react-icons/fa";
import { formatDate } from "./TimeListHelpers";
import HighlightText from "./HighlightText";
import { Link } from "react-router-dom";

const TimeEntriesTable = ({
  totalHours,
  loading,
  filteredEntries,
  selectedEntries,
  handleSelectAll,
  handleSelectEntry,
  approveEntry,
  setEditingEntry,
  startInlineEdit,
  handleInlineChange,
  saveInlineEdit,
  cancelInlineEdit,
  inlineEditing,
  projects,
  scaffoldIds,
  searchTerm,
  sortConfig,
  onSortChange,
}) => {
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "40px",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  // Function to handle sort requests
  const requestSort = (field) => {
    let direction = "asc";
    if (
      sortConfig &&
      sortConfig.field === field &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    onSortChange({ field, direction });
  };

  // Helper to get the sort direction icon
  const getSortDirectionIcon = (field) => {
    if (!sortConfig || sortConfig.field !== field) {
      return null;
    }
    return sortConfig.direction === "asc" ? (
      <FaArrowUp size={12} style={{ marginLeft: '4px' }} />
    ) : (
      <FaArrowDown size={12} style={{ marginLeft: '4px' }} />
    );
  };

  // Create a custom SortableTableCell component for consistency
  const SortableTableCell = ({ field, label, tooltip }) => (
    <TableCell>
      <Tooltip title={tooltip || `Sort by ${label}`} arrow>
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer' 
          }}
          onClick={() => requestSort(field)}
        >
          <span>{label}</span>
          {getSortDirectionIcon(field)}
        </div>
      </Tooltip>
    </TableCell>
  );

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Checkbox
                checked={
                  selectedEntries.length === filteredEntries.length &&
                  filteredEntries.length > 0
                }
                onChange={handleSelectAll}
                disabled={filteredEntries.length === 0}
                aria-label="Select all entries"
              />
            </TableCell>
            <SortableTableCell 
              field="userId.name" 
              label="User" 
              tooltip="Sort by user name"
            />
            <SortableTableCell 
              field="date" 
              label="Date" 
              tooltip="Sort by date"
            />
            <SortableTableCell 
              field="projectId.projectName" 
              label="Project" 
              tooltip="Sort by project name"
            />
            <SortableTableCell 
              field="scaffoldId.scaffoldId" 
              label="Scaffold ID" 
              tooltip="Sort by scaffold ID"
            />
            <SortableTableCell 
              field="totalHours" 
              label="Hours" 
              tooltip="Sort by total hours"
            />
            <SortableTableCell 
              field="workTypeId.name" 
              label="Work Type" 
              tooltip="Sort by work type"
            />
            <SortableTableCell 
              field="compensationTypeId.name" 
              label="Additional Compensation" 
              tooltip="Sort by compensation type"
            />
            <SortableTableCell 
              field="approvalStatus" 
              label="Status" 
              tooltip="Sort by approval status"
            />
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredEntries?.length > 0 ? (
            filteredEntries?.map((entry) => (
              <TableRow key={entry.id || entry._id}>
                <TableCell>
                  <Checkbox
                    checked={selectedEntries.includes(entry.id || entry._id)}
                    onChange={(e) =>
                      handleSelectEntry(entry.id || entry._id, e.target.checked)
                    }
                    aria-label={`Select entry for ${entry?.userId?.name || 'user'}`}
                  />
                </TableCell>
                <TableCell>
                  <HighlightText
                    text={entry?.userId?.name}
                    searchTerm={searchTerm}
                  />
                </TableCell>
                <TableCell>
                  <HighlightText
                    text={formatDate(new Date(entry.date))}
                    searchTerm={searchTerm}
                  />
                </TableCell>

                <TableCell onClick={() => startInlineEdit(entry, "projectId")}>
                  {inlineEditing?.entryId === entry._id &&
                  inlineEditing?.field === "projectId" ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Select
                        size="small"
                        fullWidth
                        value={
                          typeof entry.projectId === "object"
                            ? entry.projectId._id
                            : entry.projectId || ""
                        }
                        onChange={(e) =>
                          handleInlineChange(entry, "projectId", e.target.value)
                        }
                        autoFocus
                        aria-label="Edit project"
                      >
                        {projects && projects.length > 0 ? (
                          projects.map((project) => (
                            <MenuItem key={project._id} value={project._id}>
                              {project.projectName}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>No projects found</MenuItem>
                        )}
                      </Select>
                      <IconButton
                        size="small"
                        onClick={() => saveInlineEdit(entry)}
                        aria-label="Save edit"
                      >
                        <FaSave />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={cancelInlineEdit}
                        aria-label="Cancel edit"
                      >
                        <FaTimes />
                      </IconButton>
                    </Box>
                  ) : (
                    <Tooltip title="Click to edit project">
                      <div
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline dashed",
                        }}
                      >
                        <HighlightText
                          text={entry?.projectId?.projectName || "-"}
                          searchTerm={searchTerm}
                        />
                      </div>
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell onClick={() => startInlineEdit(entry, "scaffoldId")}>
                  {inlineEditing?.entryId === entry._id &&
                  inlineEditing?.field === "scaffoldId" ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Select
                        size="small"
                        fullWidth
                        value={
                          typeof entry.scaffoldId === "object"
                            ? entry.scaffoldId._id
                            : entry.scaffoldId || ""
                        }
                        onChange={(e) =>
                          handleInlineChange(
                            entry,
                            "scaffoldId",
                            e.target.value
                          )
                        }
                        autoFocus
                        aria-label="Edit scaffold ID"
                      >
                        {scaffoldIds
                          .filter(
                            (s) =>
                              !entry.projectId ||
                              s.projectId ===
                                (typeof entry.projectId === "object"
                                  ? entry.projectId._id
                                  : entry.projectId)
                          )
                          .map((scaffold) => (
                            <MenuItem key={scaffold._id} value={scaffold._id}>
                              {scaffold.scaffolderowner}
                            </MenuItem>
                          ))}
                      </Select>
                      <IconButton
                        size="small"
                        onClick={() => saveInlineEdit(entry)}
                        aria-label="Save edit"
                      >
                        <FaSave />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={cancelInlineEdit}
                        aria-label="Cancel edit"
                      >
                        <FaTimes />
                      </IconButton>
                    </Box>
                  ) : (
                    <Tooltip title="Click to edit scaffold ID">
                      <div
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline dashed",
                        }}
                      >
                        <HighlightText
                          text={entry?.scaffoldId?.scaffoldId || "-"}
                          searchTerm={searchTerm}
                        />
                      </div>
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell>
                  <HighlightText
                    text={entry.totalHours}
                    searchTerm={searchTerm}
                  />
                </TableCell>
                <TableCell>
                  <HighlightText
                    text={entry?.workTypeId?.name || "-"}
                    searchTerm={searchTerm}
                  />
                </TableCell>
                <TableCell>
                  <HighlightText
                    text={entry?.compensationTypeId?.name || "-"}
                    searchTerm={searchTerm}
                  />
                </TableCell>
                <TableCell>
                  <Badge
                    color={
                      entry.approvalStatus === "approved"
                        ? "primary"
                        : "secondary"
                    }
                  >
                    <HighlightText
                      text={
                        entry.approvalStatus === "approved"
                          ? "Approved"
                          : "Pending"
                      }
                      searchTerm={searchTerm}
                    />
                  </Badge>
                </TableCell>
                <TableCell>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Link
                      to={`/admin/edit-time-entry/${entry?.projectId?._id}/${entry?._id}`}
                    >
                      <Button 
                        variant="outlined" 
                        size="small"
                        aria-label="Edit entry"
                      >
                        <FaEdit />
                      </Button>
                    </Link>

                    <Button
                      variant={
                        entry.approvalStatus === "pending"
                          ? "contained"
                          : "outlined"
                      }
                      size="small"
                      disabled={entry.approvalStatus === "approved"}
                      onClick={() => approveEntry(entry._id)}
                      aria-label="Approve entry"
                    >
                      <FaCheck />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={12} style={{ textAlign: "center" }}>
                No time entries found matching the current filters
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {filteredEntries.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: 2,
            fontWeight: "bold",
            bgcolor: "#f5f5f5",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" mr={2}>
            Total Hours:
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            {totalHours}
          </Typography>
        </Box>
      )}
    </TableContainer>
  );
};

export default TimeEntriesTable;